/**
 * Cache Update Service
 * 
 * Smart cache management that replaces aggressive invalidation with selective updates.
 * Uses setQueryData instead of invalidateQueries to prevent component remounting.
 */

import { throttle, debounce } from 'lodash';

class CacheUpdateService {
  constructor() {
    if (CacheUpdateService.instance) {
      return CacheUpdateService.instance;
    }

    this.queryClient = null;
    this.updateQueue = new Map();
    this.throttledUpdates = new Map();
    this.isInitialized = false;

    // Configuration
    this.config = {
      throttleDelay: 1000, // Max 1 update per second per cache key
      debounceDelay: 300,  // Wait 300ms for batch updates
      maxQueueSize: 100,   // Max queued updates per cache key
      enableBatching: true,
      enableThrottling: true
    };

    CacheUpdateService.instance = this;
  }

  /**
   * Initialize with React Query client
   */
  initialize(queryClient) {
    if (this.isInitialized) {
      console.log('🔄 CacheUpdateService already initialized');
      return;
    }

    this.queryClient = queryClient;
    this.isInitialized = true;

    // Create throttled versions of update methods
    this.updateWalletCacheThrottled = throttle(this.updateWalletCache.bind(this), this.config.throttleDelay);
    this.updateOrderCacheThrottled = throttle(this.updateOrderCache.bind(this), this.config.throttleDelay);
    this.updateMarketCacheThrottled = throttle(this.updateMarketCache.bind(this), this.config.throttleDelay);

    console.log('🚀 CacheUpdateService initialized');

    // Set up periodic cleanup
    this.setupCleanup();
  }

  /**
   * Update wallet cache with new data
   */
  updateWalletCache(updateData) {
    if (!this.queryClient) {
      console.warn('⚠️ QueryClient not initialized');
      return;
    }

    console.log('🔄 Updating wallet cache with selective data update', updateData);

    try {
      // Get current wallet data
      const currentData = this.queryClient.getQueryData(['fetchWallet']);

      if (currentData && Array.isArray(currentData)) {
        // Handle WebSocket wallet update format: {"type":1,"id":54003,"amount":5.464996353683,"freeze":69,"value":1.8529944536578802,"toman_amount":212816.41300260753}
        if (updateData.id && updateData.type === 1) {
          // WebSocket format - find wallet by id and update
          const updatedData = currentData.map(wallet => {
            if (wallet.id === updateData.id) {
              console.log(`💰 Updating wallet ${wallet.symbol} (ID: ${wallet.id})`);
              return {
                ...wallet,
                amount: updateData.amount !== undefined ? updateData.amount : wallet.amount,
                freeze: updateData.freeze !== undefined ? updateData.freeze : wallet.freeze,
                value: updateData.value !== undefined ? updateData.value : wallet.value,
                toman_amount: updateData.toman_amount !== undefined ? updateData.toman_amount : wallet.toman_amount,
                lastUpdate: Date.now()
              };
            }
            return wallet;
          });

          // Update cache with new data (no invalidation)
          this.queryClient.setQueryData(['fetchWallet'], updatedData);
          console.log('✅ Wallet cache updated successfully via WebSocket');
        }
        // Handle legacy format with balances object
        else if (updateData.balances) {
          const updatedData = currentData.map(wallet => {
            if (updateData.balances[wallet.symbol]) {
              return {
                ...wallet,
                ...updateData.balances[wallet.symbol],
                lastUpdate: Date.now()
              };
            }
            return wallet;
          });

          // Update cache with new data (no invalidation)
          this.queryClient.setQueryData(['fetchWallet'], updatedData);
          console.log('✅ Wallet cache updated successfully');
        } else {
          console.warn('⚠️ Unknown wallet update format:', updateData);
          // Fallback to gentle invalidation
          this.queryClient.invalidateQueries(['fetchWallet'], {
            refetchType: 'none'
          });
        }
      } else {
        // If no current data, trigger a background fetch
        console.log('🔄 No current wallet data, triggering background fetch');
        this.queryClient.invalidateQueries(['fetchWallet'], {
          refetchType: 'none' // Don't refetch active queries
        });
      }
    } catch (error) {
      console.error('❌ Error updating wallet cache:', error);
      // Fallback to gentle invalidation
      this.queryClient.invalidateQueries(['fetchWallet'], {
        refetchType: 'none'
      });
    }
  }

  /**
   * Update order cache with new data
   */
  updateOrderCache(updateData) {
    if (!this.queryClient) return;

    console.log('🔄 Updating order cache with selective data update', updateData);

    try {
      // Handle WebSocket order update format: {"type":2,"id":133,"time":"2025-10-01 22:17:17","market":"TRXUSDT","order_type":"1","role":"1",...}
      if (updateData.id && updateData.type === 2) {
        // WebSocket format - update specific order
        const currentOrders = this.queryClient.getQueryData(['fetchOrder']);

        if (currentOrders && currentOrders.list && Array.isArray(currentOrders.list)) {
          const orderIndex = currentOrders.list.findIndex(order => order.id === updateData.id);

          if (orderIndex >= 0) {
            // Update existing order
            console.log(`📋 Updating order ${updateData.id} in cache`);
            const updatedList = [...currentOrders.list];
            updatedList[orderIndex] = {
              ...updatedList[orderIndex],
              ...updateData,
              lastUpdate: Date.now()
            };

            this.queryClient.setQueryData(['fetchOrder'], {
              ...currentOrders,
              list: updatedList
            });
            console.log('✅ Order cache updated successfully via WebSocket');
          } else {
            // Order not found in current list - might be new or completed
            console.log(`📋 Order ${updateData.id} not found in active orders - might be completed`);
            // Gentle invalidation to refresh the list
            this.queryClient.invalidateQueries(['fetchOrder'], { refetchType: 'none' });
            this.queryClient.invalidateQueries(['fetchOrderHistory'], { refetchType: 'none' });
          }
        } else {
          console.log('🔄 No current order data, triggering background fetch');
          this.queryClient.invalidateQueries(['fetchOrder'], { refetchType: 'none' });
        }
      }
      // Handle legacy format with orders array
      else if (updateData.orders) {
        const currentOrders = this.queryClient.getQueryData(['fetchOrder']);
        if (currentOrders) {
          const updatedOrders = this.mergeOrderData(currentOrders, updateData.orders);
          this.queryClient.setQueryData(['fetchOrder'], updatedOrders);
        }

        // Update order history if needed
        const currentHistory = this.queryClient.getQueryData(['fetchOrderHistory']);
        if (currentHistory && updateData.orderHistory) {
          const updatedHistory = this.mergeOrderData(currentHistory, updateData.orderHistory);
          this.queryClient.setQueryData(['fetchOrderHistory'], updatedHistory);
        }

        console.log('✅ Order cache updated successfully');
      } else {
        console.warn('⚠️ Unknown order update format:', updateData);
        this.queryClient.invalidateQueries(['fetchOrder'], { refetchType: 'none' });
      }
    } catch (error) {
      console.error('❌ Error updating order cache:', error);
      // Fallback to gentle invalidation
      this.queryClient.invalidateQueries(['fetchOrder'], { refetchType: 'none' });
    }
  }

  /**
   * Update market data cache
   */
  updateMarketCache(updateData) {
    if (!this.queryClient) return;

    console.log('🔄 Updating market cache with selective data update');

    try {
      // Update ticker data
      if (updateData.tickers) {
        Object.entries(updateData.tickers).forEach(([symbol, tickerData]) => {
          const cacheKey = ['ticker', symbol];
          this.queryClient.setQueryData(cacheKey, tickerData);
        });
      }

      // Update orderbook data
      if (updateData.orderbooks) {
        Object.entries(updateData.orderbooks).forEach(([symbol, orderbookData]) => {
          const cacheKey = ['orderbook', symbol];
          this.queryClient.setQueryData(cacheKey, orderbookData);
        });
      }

      console.log('✅ Market cache updated successfully');
    } catch (error) {
      console.error('❌ Error updating market cache:', error);
    }
  }

  /**
   * Merge order data intelligently
   */
  mergeOrderData(currentData, newData) {
    if (!currentData || !Array.isArray(currentData)) return newData;
    if (!newData || !Array.isArray(newData)) return currentData;

    const merged = [...currentData];
    
    newData.forEach(newOrder => {
      const existingIndex = merged.findIndex(order => order.id === newOrder.id);
      
      if (existingIndex >= 0) {
        // Update existing order
        merged[existingIndex] = { ...merged[existingIndex], ...newOrder };
      } else {
        // Add new order
        merged.unshift(newOrder);
      }
    });

    // Sort by timestamp (newest first) and limit to reasonable size
    return merged
      .sort((a, b) => new Date(b.created_at || b.timestamp) - new Date(a.created_at || a.timestamp))
      .slice(0, 100);
  }

  /**
   * Smart invalidation for specific operations
   */
  invalidateForOperation(operationType, operationData = {}) {
    if (!this.queryClient) return;

    console.log(`🔄 Smart invalidation for operation: ${operationType}`);

    switch (operationType) {
      case 'TRADE_EXECUTED':
        // Only invalidate wallet and orders, not market data
        this.gentleInvalidate(['fetchWallet']);
        this.gentleInvalidate(['fetchOrder']);
        break;

      case 'DEPOSIT_COMPLETED':
        // Only invalidate wallet and transaction history
        this.gentleInvalidate(['fetchWallet']);
        this.gentleInvalidate(['fetchDepositTransanction']);
        break;

      case 'WITHDRAWAL_PROCESSED':
        // Only invalidate wallet and withdrawal history
        this.gentleInvalidate(['fetchWallet']);
        this.gentleInvalidate(['fetchWithDrawTransactions']);
        break;

      case 'ORDER_CANCELLED':
      case 'ORDER_CANCEL':
        // 🔥 CRITICAL FIX: Clear in-memory order cache
        if (typeof window !== 'undefined' && window.orderCache) {
          const cacheSize = window.orderCache.size;
          window.orderCache.clear();
          console.log(`🗑️ Cleared in-memory order cache (${cacheSize} entries) for ORDER_CANCEL`);
        }

        // Only invalidate orders
        this.gentleInvalidate(['fetchOrder']);
        this.gentleInvalidate(['fetchOrderHistory']);
        break;

      case 'WEBSOCKET_UPDATE':
        // Invalidate queries to refetch fresh data from API
        // This is more reliable than trying to update individual records
        if (operationData.type === 'wallet') {
          console.log('💰 Wallet update from WebSocket - refetching wallet data from API');
          this.forceRefresh(['fetchWallet']);
          this.forceRefresh(['fetchWalletSummary']);

          // ✅ Also invalidate transaction history tables (deposit/withdraw)
          // These need to be updated when wallet balance changes
          console.log('🔄 Invalidating transaction history due to wallet update');
          this.queryClient.invalidateQueries(['fetchDepositTransanction'], {
            refetchType: 'active' // Only refetch if component is mounted and active
          });
          this.queryClient.invalidateQueries(['fetchWithDrawTransactions'], {
            refetchType: 'active'
          });
          this.queryClient.invalidateQueries(['fetchCryptoTransactionHistory'], {
            refetchType: 'active'
          });
        } else if (operationData.type === 'order') {
          console.log('📋 Order update from WebSocket - refetching order data from API');
          this.forceRefresh(['fetchOrder']);
          this.forceRefresh(['fetchOrderHistory']);
        } else if (operationData.type === 'market') {
          console.log('📊 Market update from WebSocket - refetching market data from API');
          this.forceRefresh(['fetchMarketData']);
        }
        break;

      default:
        console.warn(`⚠️ Unknown operation type: ${operationType}`);
        break;
    }
  }

  /**
   * Gentle invalidation that doesn't force refetch
   */
  gentleInvalidate(queryKey) {
    if (!this.queryClient) return;

    this.queryClient.invalidateQueries(queryKey, {
      refetchType: 'none', // Don't refetch immediately
      exact: false // Allow partial matches
    });

    console.log(`🔄 Gentle invalidation for: ${JSON.stringify(queryKey)}`);
  }

  /**
   * Force refresh for critical operations
   */
  forceRefresh(queryKey) {
    if (!this.queryClient) return;

    // ✅ CRITICAL FIX: Clear in-memory order cache when invalidating order queries
    // This ensures fresh API calls instead of using cached promises
    if (queryKey[0] === 'fetchOrder' && typeof window !== 'undefined' && window.orderCache) {
      const cacheSize = window.orderCache.size;
      window.orderCache.clear();
      console.log(`🗑️ Cleared in-memory order cache (${cacheSize} entries) during force refresh`);
    }

    this.queryClient.invalidateQueries(queryKey, {
      refetchType: 'active', // Refetch active queries
      exact: false
    });

    console.log(`🔄 Force refresh for: ${JSON.stringify(queryKey)}`);
  }

  /**
   * Batch multiple updates together
   */
  batchUpdate(updates) {
    if (!this.config.enableBatching) {
      updates.forEach(update => this.processUpdate(update));
      return;
    }

    console.log(`🔄 Batching ${updates.length} cache updates`);

    // Group updates by type
    const groupedUpdates = updates.reduce((groups, update) => {
      const type = update.type || 'default';
      if (!groups[type]) groups[type] = [];
      groups[type].push(update);
      return groups;
    }, {});

    // Process each group
    Object.entries(groupedUpdates).forEach(([type, typeUpdates]) => {
      this.processBatchedUpdates(type, typeUpdates);
    });
  }

  /**
   * Process batched updates of the same type
   */
  processBatchedUpdates(type, updates) {
    switch (type) {
      case 'wallet':
        const walletData = updates.reduce((merged, update) => ({
          ...merged,
          ...update.data
        }), {});
        if (this.updateWalletCacheThrottled) {
          this.updateWalletCacheThrottled(walletData);
        } else {
          this.updateWalletCache(walletData);
        }
        break;

      case 'order':
        const orderData = updates.reduce((merged, update) => ({
          orders: [...(merged.orders || []), ...(update.data.orders || [])],
          orderHistory: [...(merged.orderHistory || []), ...(update.data.orderHistory || [])]
        }), {});
        if (this.updateOrderCacheThrottled) {
          this.updateOrderCacheThrottled(orderData);
        } else {
          this.updateOrderCache(orderData);
        }
        break;

      case 'market':
        const marketData = updates.reduce((merged, update) => ({
          ...merged,
          ...update.data
        }), {});
        if (this.updateMarketCacheThrottled) {
          this.updateMarketCacheThrottled(marketData);
        } else {
          this.updateMarketCache(marketData);
        }
        break;

      default:
        updates.forEach(update => this.processUpdate(update));
        break;
    }
  }

  /**
   * Process individual update
   */
  processUpdate(update) {
    const { type, operation, data } = update;

    if (operation) {
      this.invalidateForOperation(operation, data);
    } else if (type === 'wallet') {
      if (this.updateWalletCacheThrottled) {
        this.updateWalletCacheThrottled(data);
      } else {
        this.updateWalletCache(data);
      }
    } else if (type === 'order') {
      if (this.updateOrderCacheThrottled) {
        this.updateOrderCacheThrottled(data);
      } else {
        this.updateOrderCache(data);
      }
    } else if (type === 'market') {
      if (this.updateMarketCacheThrottled) {
        this.updateMarketCacheThrottled(data);
      } else {
        this.updateMarketCache(data);
      }
    }
  }

  /**
   * Get cache statistics
   */
  getCacheStats() {
    if (!this.queryClient) return null;

    const cache = this.queryClient.getQueryCache();
    const queries = cache.getAll();

    return {
      totalQueries: queries.length,
      activeQueries: queries.filter(q => q.observers.length > 0).length,
      staleQueries: queries.filter(q => q.isStale()).length,
      errorQueries: queries.filter(q => q.state.status === 'error').length,
      memoryUsage: this.estimateMemoryUsage(queries)
    };
  }

  /**
   * Estimate memory usage (rough calculation)
   */
  estimateMemoryUsage(queries) {
    let totalSize = 0;
    queries.forEach(query => {
      if (query.state.data) {
        try {
          totalSize += JSON.stringify(query.state.data).length;
        } catch (e) {
          // Ignore circular references
        }
      }
    });
    return `${(totalSize / 1024 / 1024).toFixed(2)} MB`;
  }

  /**
   * Setup periodic cleanup
   */
  setupCleanup() {
    // Clean up old queue entries every 5 minutes
    setInterval(() => {
      this.updateQueue.clear();
      console.log('🧹 Cleaned up cache update queue');
    }, 5 * 60 * 1000);
  }

  /**
   * Clear all caches (emergency reset)
   */
  clearAllCaches() {
    if (!this.queryClient) return;

    this.queryClient.clear();
    console.log('🧹 All caches cleared');
  }
}

// Export singleton instance
const cacheUpdateService = new CacheUpdateService();
export default cacheUpdateService;
